import Axios from 'axios';
import { Subject } from '@/models/subject';
import { Person } from '@/models/person';
import moment from 'moment';

interface SchedulesRequest {
  studentId: string;
  year: number;
  month: number; // 1-12
}

interface CancelRequest {
  studentId: string;
  scheduleId: number;
}

interface ReserveRequest {
  calendarId: number;
  scheduleId: number;
  studentId: string;
  subjectId: number;
  isForce: boolean;
}

interface Schedule {
  calendarId: number;
  scheduleId: number;
  unixtimeDate: number;
  stringStart: string;
  stringEnd: string;
  subject: Subject;
  capacity: 0;
  reservers: Person[];
  memo: '';
  tutorName: string;
}
const emptySchedule = (): Schedule => {
  return {
    calendarId: 0,
    scheduleId: 0,
    unixtimeDate: 0,
    stringStart: '',
    stringEnd: '',
    subject: { id: 0, name: '', color: '' },
    capacity: 0,
    reservers: [],
    memo: '',
    tutorName: '',
  };
};

interface ReservationRequest {
  year: number;
  month: number;
  studentId: string;
  subjectId: number;
  tutorId: string;
}

interface StudentReservable {
  isMonthCheck: boolean;
  startDate: Date;
  endDate: Date;
  reservableCount: number;
}

// 型の Export
export {
  Schedule,
  SchedulesRequest,
  emptySchedule,
  ReservationRequest,
  StudentReservable,
};

interface State {
  isLoading: boolean;
  schedules: Schedule[];
  schedulesRequest: SchedulesRequest;
  reservationRequest: ReservationRequest;
  reservationSchedules: Schedule[];
  studentReservableList: StudentReservable[];
}

const mapState = {
  isLoading: false,
  schedules: [],
  schedulesRequest: {
    studentId: '',
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  },
  reservationRequest: {
    studentId: '',
    year: 0,
    month: 0,
    subjectId: -1,
    tutorId: '',
  },
  reservationSchedules: [],
  studentReservableList: [],
};

const mapMutations = {
  setIsLoading(state: State, isLoading: boolean) {
    state.isLoading = isLoading;
  },

  setSchedulesRequest(state: State, request: SchedulesRequest) {
    state.schedulesRequest = request;
  },

  setSchedules(state: State, schedules: Schedule[]) {
    state.schedules = schedules;
  },

  setReservationSchedules(state: State, schedules: Schedule[]) {
    state.reservationSchedules = schedules;
  },

  setStudentReservableList(state: State, studentReservableList: StudentReservable[]) {
    state.studentReservableList = studentReservableList;
  },
};

const mapActions = {
  async getSchedulesRequest({ commit, state }: any) {
    commit('setIsLoading', true);

    const response = await Axios.post<Schedule[]>('/api/Reservation/StudentCalendar', state.schedulesRequest)
      .catch((error) => {
        commit('setIsLoading', false);
        commit('setSchedules', []);
        throw error;
      });
    if (response) {
      commit('setSchedules', response.data);
    }
    commit('setIsLoading', false);
  },

  async getStudentReservableList({ commit, state }: any) {
    commit('setIsLoading', true);

    const response = await Axios.post('/api/Reservation/StudentReservableList', state.schedulesRequest)
      .catch((error) => {
        commit('setIsLoading', false);
        commit('setStudentReservableList', []);
        throw error;
      });
    if (response) {
      const studentReservableList = response.data.list;
      studentReservableList.forEach((item: any) => {
        item.startDate = moment(item.startDate).toDate();
        item.endDate = moment(item.endDate).toDate();
      });
      commit('setStudentReservableList', studentReservableList);
    }
    commit('setIsLoading', false);
  },

  async getReservationSchedules({ commit, state }: any) {
    commit('setIsLoading', true);
    const response = await Axios.post('/api/Reservation/ReservableSchedules', state.reservationRequest)
      .catch((error) => {
        commit('setIsLoading', false);
        commit('setReservationSchedules', []);
        throw error;
      });
    if (response) {
      commit('setReservationSchedules', response.data);
    }
    commit('setIsLoading', false);
  },

  async cancelReservation({ commit, state}: any, request: CancelRequest) {
    commit('setIsLoading', true);
    const response = await Axios.post('/api/Reservation/DeleteReserve', request).catch((error) => {
      commit('setIsLoading', false);
      throw error;
    });
    commit('setIsLoading', false);
    return response.data;
  },

  async sendReservation({ commit, state}: any, request: ReserveRequest) {
    commit('setIsLoading', true);
    const response = await Axios.post('/api/Reservation/Reserve', request).catch((error) => {
      commit('setIsLoading', false);
      throw error;
    });
    commit('setIsLoading', false);
    return response.data;
  },

  async sendBulkReservation({ commit, state}: any, request: ReserveRequest[]) {
    commit('setIsLoading', true);
    const response = await Axios.post('/api/Reservation/BulkReserve', request).catch((error) => {
      commit('setIsLoading', false);
      throw error;
    });
    commit('setIsLoading', false);
    return response.data;
  },
};

// ストアの Export
export default {
  namespaced: true,
  state: mapState,
  actions: mapActions,
  mutations: mapMutations,
};
